import { IApiHandlerParams, IApiMethods, TApiResponse, TRequestMethods } from "@/api/interface";
import { AxiosResponse } from "axios";
import { API_DEF_CONFIG } from "@/api/config";
import {
	successApiStatusCodes,
	skipApiStatusCodes,
	autoLogoutApiStatusCodes,
	refreshTokenApiStatusCodes,
	successRequestStatusCodes
} from "@/api/status-code";
import store from '@/store';
import { LOADING_MODULE_NAME } from "@/store/modules/loading";
import i18n from '../plugins/i18n/i18n';
import { $logout, $refreshToken } from "@/api/auth/auth";
import auth from "@/models/modules/auth";
import router from "@/router";
import NotificationCore from "@/modules/NotificationCore/NotificationCore";
import { RefreshTokenHandler } from "@/api/types";
import AppConfig from "@/core/app.config";
import api from "@/api/index";
import { AUTH_MODULE_NAME } from "@/store/modules/auth";

const apiNonBodyMethods: TRequestMethods[] = [
	"get",
	"delete"
];

const countRefreshRequest: number = store.getters[`${AUTH_MODULE_NAME}/countRefreshRequest`];

const setCountRefreshRequestChange = (count: number) =>
	store.commit(`${AUTH_MODULE_NAME}/setCountRefreshRequestChange`, count);

const resetCountRefreshRequest = (time: number) =>
	setTimeout(() => setCountRefreshRequestChange(0), time);

export const refreshTokenHandler: RefreshTokenHandler = {
	currentHandler: null,
	refreshIsAvailable: true,
	async updateToken() {
		if (this.refreshIsAvailable) {
			this.refreshIsAvailable = false;

			this.currentHandler = new Promise(async (resolve) => {
				const tokenRefreshed = await this.getNewToken();

				this.refreshIsAvailable = true;

				resolve(tokenRefreshed);
			});
		}

		return this.currentHandler;
	},
	async getNewToken(): Promise<boolean> {
		setCountRefreshRequestChange(countRefreshRequest + 1)

		const response = await $refreshToken();

		resetCountRefreshRequest(60000);

		if (response && countRefreshRequest < 3) return true;

		await $logout();

		resetCountRefreshRequest(1000);

		return false;
	}
};

export const loadingIsActive = (): boolean =>
	store.getters[`${LOADING_MODULE_NAME}/isActive`];

export const setLoading = (isActive: boolean) =>
	store.commit(`${LOADING_MODULE_NAME}/setActive`, isActive);

const hideLoading = (config: IApiHandlerParams) => {
	if (config.disableLoader !== true && loadingIsActive()) setLoading(false);
};

export const activateLoading = (_config?: IApiHandlerParams) =>
	(!_config || _config.disableLoader !== true) && setLoading(true);

export const addToast = NotificationCore.addToast;

export const isSuccessApiStatusCode = (statusCode: number): boolean =>
	successApiStatusCodes.includes(statusCode);

export const isSkipApiStatusCode = (statusCode: number): boolean =>
	skipApiStatusCodes.includes(statusCode);

export const isSuccessResponseStatusCode = (statusCode: number): boolean =>
	(isSuccessApiStatusCode(statusCode) || isSkipApiStatusCode(statusCode));

export function getFullConfig(config?: IApiHandlerParams): IApiHandlerParams {
	return Object.assign({}, API_DEF_CONFIG, config);
}

export function isSuccessResponse(r: AxiosResponse): boolean {
	try {
		return (
			successRequestStatusCodes.includes(r.status) &&
			isSuccessResponseStatusCode(r.data.status)
		);
	} catch (err) {
		console.warn('isSuccessResponse()', err);

		return false;
	}
}

export function handleErrorResponseData(r: TApiResponse, _config?: IApiHandlerParams) {
	if (!r || !r.status) return;

	const config: IApiHandlerParams = getFullConfig(_config);

	const currentRoute: boolean = (router.currentRoute && router.currentRoute.name || '') === 'Login';

	try {
		if (autoLogoutApiStatusCodes.includes(r.status as number) && !currentRoute) {
			auth.signOut();

			router.push({ name: 'Login' }).catch(() => {});
		} else if (refreshTokenApiStatusCodes.includes(r.status as number) && !currentRoute) {
			$refreshToken().then(r => {
				if (!r) {
					auth.signOut();

					router.push({ name: 'Login' }).catch(() => {});
				}
			});
		} else if (!config.dontShowErrorMessage) {
			addToast(
				r.errorMessage || i18n.t('errors.error') as string,
				'error',
				false // TODO: Before need template implement !!config.showModalOnError
			);
		}
	} catch (err) {
		console.warn(err);
	}
}

export async function requestHandler(r: AxiosResponse, _config?: IApiHandlerParams): Promise<TApiResponse> {
	const config: IApiHandlerParams = getFullConfig(_config);

	hideLoading(config);

	if (!isSuccessResponse(r)) handleErrorResponseData(r.data, config);

	try {
		return config.returnData ? r.data?.data : r.data;
	} catch (err) {
		console.warn('requestHandler()', err);

		return null;
	}
}

export async function handleErrorResponse(_config?: IApiHandlerParams, r: TApiResponse = null): Promise<TApiResponse> {
	const config: IApiHandlerParams = getFullConfig(_config || {});

	hideLoading(config);

	if (!r || !r.response) return;

	if (r.response.status === 401 && !config.ignoreErrorHandler) {
		config.dontShowErrorMessage = true;

		const tokenRefreshed = await refreshTokenHandler.updateToken();

		if (tokenRefreshed) {
			const apiMethod = api[r.response.config.method as keyof IApiMethods].bind(api);
			const apiConfig = { handler: r.response.config.API_HANDLER_CONFIG, request: r.response.config };

			if (apiNonBodyMethods.includes(r.response.config.method))
				return apiMethod(r.response.config.API_ENDPOINT, apiConfig);

			return apiMethod(r.response.config.API_ENDPOINT, r.response.config.data, apiConfig);
		}
	}

	if (config.dontShowErrorMessage !== true)
		await addToast(i18n.t("errors.error") as string, "error");

	if (
		AppConfig.autoLogoutEnabled
			&& !config.ignoreErrorHandler
			&& !(r instanceof Error)
			&& r?.response.status === 401
	) await $logout();

	return r.isAxiosError ? null : r;
}
